<template>
    <div class="view">
        <system-message
            :success="success"
            :message="message"
            class="uk-container"
        />
        <div class="uk-padding">
            <span class="uk-text-uppercase uk-text-small uk-text-bold">Settings for:</span>
            <multiselect-field
                v-model="buildRegionStateId"
                placeholder="Please select one."
                :options="states"
                track-by="id"
                label="title"
                name="state"
                :put-icon-after-input="true"
                focus
                :multiple="false"
                :show-labels="false"
                :hide-selected="false"
                :show-error-on-blur-only="true"
            />
        </div>
        <div
            v-if="buildRegionStateId"
            class="uk-padding uk-padding-remove-top"
        >
            <div class="uk-text-uppercase uk-text-small uk-text-bold uk-margin-bottom">
                Email Notifications
            </div>
            <div class="uk-margin-bottom">
                When the refund is <b>finalised</b>, send an email notification to:
            </div>
            <div class="uk-margin-bottom">
                <ol>
                    <li>Requester</li>
                    <li>Approver</li>
                    <li
                        v-for="recipient in refundFinalisedEmailsRecipients"
                        :key="recipient.id"
                    >
                        <payments-app-settings-form
                            :id="recipient.id"
                            :build-region-state-id="recipient.build_region_state_id"
                            :app-id="appId"
                            :type="recipient.type"
                            :value="recipient.value"
                            :validation-methods="validationMethodsForEmailTypes"
                            @updated="getPaymentsAppSettings"
                        />
                    </li>
                    <li>
                        <payments-app-settings-form
                            :key="'refundFinalisedEmailsRecipients-' + refundFinalisedEmailsRecipientsKey"
                            :build-region-state-id="buildRegionStateId"
                            :app-id="appId"
                            :type="paymentsAppSettingsTypes.REFUND_FINALISED_EMAILS_RECIPIENT"
                            :validation-methods="validationMethodsForEmailTypes"
                            @updated="updateRefundFinalisedEmailsRecipientsKeyAndGetPaymentsAppSettings"
                        />
                    </li>
                </ol>
            </div>
            <div class="uk-margin-bottom">
                When there's an <b>error</b>, send an email notification to:
            </div>
            <div class="uk-margin-bottom">
                <ol>
                    <li
                        v-for="recipient in refundErrorEmailsRecipients"
                        :key="recipient.id"
                    >
                        <payments-app-settings-form
                            :id="recipient.id"
                            :build-region-state-id="recipient.build_region_state_id"
                            :app-id="appId"
                            :type="recipient.type"
                            :value="recipient.value"
                            :validation-methods="validationMethodsForEmailTypes"
                            @updated="getPaymentsAppSettings"
                        />
                    </li>
                    <li>
                        <payments-app-settings-form
                            :key="'refundErrorEmailsRecipients-' + refundErrorEmailsRecipientsKey"
                            :build-region-state-id="buildRegionStateId"
                            :app-id="appId"
                            :type="paymentsAppSettingsTypes.REFUND_ERROR_EMAILS_RECIPIENT"
                            :validation-methods="validationMethodsForEmailTypes"
                            @updated="updateRefundErrorEmailsRecipientsKeyAndGetPaymentsAppSettings"
                        />
                    </li>
                </ol>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { getBuildRegionStates, getPaymentsAppSettings, getApps } from "../helpers/api";
import { PaymentsAppSettingsType, AppName } from "../helpers/constants";
import { required, email } from "vuelidate/lib/validators";

const MultiselectField = () => import( "@strangeanimals/common-vue-components/src/components/fields/MultiselectField");
const PaymentsAppSettingsForm = () => import("../components/forms/PaymentsAppSettingsForm");
const SystemMessage  = () => import("@strangeanimals/common-vue-components/src/components/page/SystemMessage");

export default {
    name: 'SettingsView',
    components: { MultiselectField, PaymentsAppSettingsForm, SystemMessage },
    data() {
        return {
            states: [],
            buildRegionStateId: null,
            paymentsAppSettings: [],
            success: true,
            message: [],
            paymentsAppSettingsTypes: PaymentsAppSettingsType,
            refundFinalisedEmailsRecipientsKey: 0,
            refundErrorEmailsRecipientsKey: 0
        }
    },
    computed: {
        ...mapGetters({
            canUserAccessSettings: 'canUserAccessSettings',
            appId: 'appId'
        }),
        refundFinalisedEmailsRecipients: function () {
            let result = this.paymentsAppSettings.filter((settings) => { return settings.type === PaymentsAppSettingsType.REFUND_FINALISED_EMAILS_RECIPIENT});
            return result;
        },
        refundErrorEmailsRecipients: function () {
            let result = this.paymentsAppSettings.filter((settings) => { return settings.type === PaymentsAppSettingsType.REFUND_ERROR_EMAILS_RECIPIENT});
            return result;
        },
        validationMethodsForEmailTypes() {
            return { required, email };
        }
    },
    watch: {
        buildRegionStateId: function () {
            if (this.buildRegionStateId) {
                this.getPaymentsAppSettings();
            } else {
                this.paymentsAppSettings = [];
            }
        }
    },
    mounted() {
        if (this.canUserAccessSettings) {
            if (this.states.length === 0) {
                this.getBuildRegionStates();
            }

            if (!this.appId) {
                this.getApps();
            }
        } else {
            this.$router.push({
                name: 'Dashboard'
            })
        }
    },
    methods: {
        ...mapActions({
            updateAppId: 'updateAppId'
        }),
        setSystemMessage(message, isSuccess) {
            this.message = message;
            this.success = isSuccess;
        },
        getBuildRegionStates() {
            let params = {
                per_page: 20,
                order_by: 'title:asc',
                status: 'enabled'
            };
            const self = this;
            getBuildRegionStates(params).then((response) => {
                if (response.status === 200) {
                    this.states = response.body;
                } else {
                    self.setSystemMessage(["There's an error in fetching the states list."], false);
                }
            });
        },
        getPaymentsAppSettings() {
            let params = {
                per_page: 1000,
                page: 1,
                build_region_state_id: this.buildRegionStateId,
                app_name: AppName
            };
            const self = this;
            getPaymentsAppSettings(params).then((response) => {
                if (response.status === 200) {
                    this.paymentsAppSettings = response.body;
                } else {
                    self.setSystemMessage(["There's an error in fetching the payments app settings list."], false);
                }
            });
        },
        getApps() {
            let params = {
                per_page: 20,
                order_by: 'title:asc',
                name: AppName
            };
            const self = this;
            getApps(params).then((response) => {
                if (response.status === 200) {
                    let app = response.body[0];
                    if (app) {
                        this.updateAppId(app.id);
                    }
                } else {
                    self.setSystemMessage(["There's an error in fetching the apps list."], false);
                }
            });
        },
        updateRefundFinalisedEmailsRecipientsKeyAndGetPaymentsAppSettings() {
            this.refundFinalisedEmailsRecipientsKey++;
            this.getPaymentsAppSettings();
        },
        updateRefundErrorEmailsRecipientsKeyAndGetPaymentsAppSettings() {
            this.refundErrorEmailsRecipientsKey++;
            this.getPaymentsAppSettings();
        }
    }
}
</script>
